import { user } from '@angular/fire/auth';
import { ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { UserEntity } from 'src/app/models/entities/user.entity';
import { UserService } from 'src/app/services/api/user.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ProfileEditComponent } from './profile-edit/profile-edit.component';
import { FileUploadService } from 'src/app/services/file-upload.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { MenuService } from 'src/app/services/heading-service';
import { TenantService } from 'src/app/services/tenant.service';
import { TenantSelectorComponent } from '../tenant-selector/tenant-selector.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ThemeService, ThemeType, BackgroundVariant, BackgroundOption } from 'src/app/services/theme.service';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface ProfileComponentData { }

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false
})
export class ProfileComponent implements OnDestroy {
  public user: UserEntity | null = null;
  public preview: string = '';
  public rank: number = 0;
  private destroy$ = new Subject<void>();

  constructor(
    public userService: UserService,
    public fileLoadService: FileUploadService,
    public dialogRef: MatDialogRef<ProfileComponent>,
    public dialog: MatDialog,
    public auth: AngularFireAuth,
    private menuService: MenuService,
    public router: Router,
    public tenantService: TenantService,
    public themeService: ThemeService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: ProfileComponentData
  ) {
    userService.currentUser$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((user) => {
      if (user) {
        this.user = user;
      }
    });

    this.userService.getLeaderboard().pipe(
      takeUntil(this.destroy$)
    ).subscribe(users => {
      if (this.user) {
        const sortedUsers = users.sort((a, b) => (b.totalScore || 0) - (a.totalScore || 0));
        const userIndex = sortedUsers.findIndex(u => u.key === this.user?.key);
        this.rank = userIndex !== -1 ? userIndex + 1 : 0;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async selectTenant() {
    
    const user = await this.auth.currentUser;
    if (user) {
      const tokenResult = await user.getIdTokenResult(true); // fresh claims before reading tenants
      const tenants = tokenResult.claims['tenants'];
      
      if (Array.isArray(tenants) && tenants.length > 0) {

        const tenantDetailsArray = await Promise.all(
          tenants.map(id => this.userService.getTenantDetails(id))
        );

        // Filter out null values (deleted tenants)
        const tenantDetails = tenantDetailsArray.filter(t => t !== null);

        if (tenantDetails.length === 0) {
          this.snackBar.open('No valid tenants found. Please contact support.', 'Close', { duration: 5000 });
          return;
        }

        // Auto-select if only one valid tenant
        if (tenantDetails.length === 1) {
          const user = this.userService.currentUser;

          this.tenantService.reloadApp();
          this.userService.resetCurrentUser();
          this.userService.setCurrentUser(user);
          this.tenantService.setTenant(tenantDetails[0]);
          this.userService.saveLastSelectedTenant(user.uid, tenantDetails[0].id); // durable, cross-device
          this.router.navigateByUrl('/');
          return;
        }

        // Show selector only if multiple valid tenants
        const dialogRef = this.dialog.open(TenantSelectorComponent, {
          width: '400px',
          data: { tenants: tenantDetails },
          disableClose: true
        });

        dialogRef.afterClosed().subscribe(async result => {
          if (result) {
            var user = this.userService.currentUser

            this.tenantService.reloadApp();
            this.userService.resetCurrentUser();
            this.userService.setCurrentUser(user);
            this.tenantService.setTenant(result);
            const fbUser = await this.auth.currentUser;
            if (fbUser) this.userService.saveLastSelectedTenant(fbUser.uid, result.id); // durable, cross-device
            this.router.navigateByUrl('/');

          }
        });
      }
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  public editPhoto(): void {
    const ref = this.dialog.open(ProfileEditComponent, {
      data: this.user,
    });

    ref.afterClosed().subscribe((result) => {
      if (result && result.image && this.user) {
        this.preview = result.preview;

        this.fileLoadService
          .saveProfile(this.user, result.image, result.type)
          .then((url) => {
            if (this.user) {
              this.user.profile = url;
              this.userService.currentUser = this.user;
              this.userService.update(this.user).then(() => {
                this.userService.profileImageChanged$.next(url);
              });
            }
          });
      }
    });
  }

  /**
   * Select and apply a theme
   */
  public selectTheme(theme: ThemeType): void {
    this.themeService.setTheme(theme);
  }

  /**
   * Get available background options for a theme
   */
  public getBackgroundOptions(theme: ThemeType): BackgroundOption[] {
    return this.themeService.getBackgroundOptions(theme);
  }

  /**
   * Check if a background is currently selected
   */
  public isBackgroundSelected(theme: ThemeType, background: BackgroundVariant): boolean {
    const currentTheme = this.themeService.getCurrentTheme();
    const currentBackground = this.themeService.getCurrentBackground();
    return currentTheme === theme && currentBackground === background;
  }

  /**
   * Select and apply a background for the current theme
   */
  public selectBackground(theme: ThemeType, background: BackgroundVariant): void {
    this.themeService.setThemeAndBackground(theme, background);
  }

  async logout() {
    try {
      // Clear user data first
      this.userService.currentUser = null;
      this.userService.resetCurrentUser();

      // Clear specific auth-related items from localStorage
      localStorage.removeItem('googleAccessToken');
      localStorage.removeItem('googleRefreshToken');
      localStorage.removeItem('googleExpiration');
      localStorage.removeItem('tenantId');
      localStorage.removeItem('redirectUrl');
      localStorage.removeItem('lastBoardId');

      // Sign out from Firebase
      await this.auth.signOut();

      // Close the dialog
      this.close();

      // Navigate to login
      await this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error during logout:', error);
      // Force navigation to login even if sign out fails
      this.router.navigate(['/login']);
    }
  }
}